import { useEffect, useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { api } from "@/lib/apiClient";

const EMPTY_FORM = {
  name: "",
  article_number_pattern: "",
  sort_order: "0",
  for_quick_sale: false,
};

export interface CreateArticleCategoryModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

export function CreateArticleCategoryModal({
  open,
  onClose,
  onCreated,
}: CreateArticleCategoryModalProps) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setForm(EMPTY_FORM);
    setError(null);
    setSubmitting(false);
  }, [open]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.post("/api/v1/article-categories", {
        name: form.name.trim(),
        article_number_pattern: form.article_number_pattern.trim(),
        sort_order: Number(form.sort_order) || 0,
        for_quick_sale: form.for_quick_sale,
      });
      onCreated();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Fehler beim Anlegen");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Neue Artikelkategorie"
      description="Name und Nummernschema für Artikel in dieser Kategorie"
    >
      <form onSubmit={(e) => void onSubmit(e)} className="space-y-4">
        <label className="flex flex-col gap-1 text-sm">
          Name
          <input
            required
            autoFocus
            className="eh-input"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          Nummernschema
          <input
            required
            className="eh-input font-mono"
            placeholder="z. B. SP-####"
            value={form.article_number_pattern}
            onChange={(e) =>
              setForm((f) => ({ ...f, article_number_pattern: e.target.value }))
            }
          />
          <span className="text-xs text-label-secondary">
            # steht für eine Ziffer der laufenden Nummer (#### = vierstellig).
          </span>
        </label>
        <label className="flex flex-col gap-1 text-sm">
          Sortierung
          <input
            type="number"
            className="eh-input w-32"
            value={form.sort_order}
            onChange={(e) => setForm((f) => ({ ...f, sort_order: e.target.value }))}
          />
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.for_quick_sale}
            onChange={(e) => setForm((f) => ({ ...f, for_quick_sale: e.target.checked }))}
          />
          Für Schnellverkauf (Shop / Lebensmittel)
        </label>
        {error ? <p className="text-sm text-status-red">{error}</p> : null}
        <div className="eh-modal-actions flex flex-wrap justify-end gap-2 border-t border-separator pt-4">
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            Abbrechen
          </Button>
          <Button type="submit" loading={submitting}>
            Anlegen
          </Button>
        </div>
      </form>
    </Modal>
  );
}
